import React, { useState } from 'react';
import { Search, Download } from 'lucide-react';
import { mockTransactions } from '../../data/mockData';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const AccountStatement = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10)
  });
  const [filteredTransactions, setFilteredTransactions] = useState(mockTransactions);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    const filtered = mockTransactions.filter(transaction => {
      const transactionDate = new Date(transaction.date);
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      return transactionDate >= start && transactionDate <= end;
    });
    setFilteredTransactions(filtered);
    toast.success(t('accountStatement.foundTransactions', { count: filtered.length }));
  };

  const handleExport = () => {
    toast.info(t('accountStatement.exportComingSoon'));
  };

  const totalIncome = filteredTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpense = filteredTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className={`max-w-6xl mx-auto p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
        {t('accountStatement.title')}
      </h1>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('accountStatement.startDate')}
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('accountStatement.endDate')}
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
            />
          </div>
          <button
            onClick={handleSearch}
            className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
          >
            <Search size={20} className={isRTL ? 'ml-2' : 'mr-2'} />
            {t('accountStatement.search')}
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center justify-center px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            <Download size={20} className={isRTL ? 'ml-2' : 'mr-2'} />
            {t('accountStatement.export')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
          <h3 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-2">
            {t('accountStatement.totalIncome')}
          </h3>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">
            ${totalIncome.toLocaleString()}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
            {t('accountStatement.totalExpense')}
          </h3>
          <p className="text-3xl font-bold text-red-600 dark:text-red-400">
            ${totalExpense.toLocaleString()}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
          <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">
            {t('accountStatement.netBalance')}
          </h3>
          <p className={`text-3xl font-bold ${
            (totalIncome - totalExpense) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}>
            ${(totalIncome - totalExpense).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          {t('accountStatement.transactionHistory')}
        </h2>
        
        <div className="app-table-wrap">
          <table className="app-table w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className={`${isRTL ? 'text-right' : 'text-left'} py-3 px-4 font-semibold text-gray-900 dark:text-white`}>{t('accountStatement.date')}</th>
                <th className={`${isRTL ? 'text-right' : 'text-left'} py-3 px-4 font-semibold text-gray-900 dark:text-white`}>{t('accountStatement.description')}</th>
                <th className={`${isRTL ? 'text-right' : 'text-left'} py-3 px-4 font-semibold text-gray-900 dark:text-white`}>{t('accountStatement.agent')}</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">{t('accountStatement.type')}</th>
                <th className={`${isRTL ? 'text-left' : 'text-right'} py-3 px-4 font-semibold text-gray-900 dark:text-white`}>{t('accountStatement.amount')}</th>
                <th className={`${isRTL ? 'text-right' : 'text-left'} py-3 px-4 font-semibold text-gray-900 dark:text-white`}>{t('accountStatement.paymentType')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((transaction) => (
                <tr key={transaction.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <td className={`py-3 px-4 text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{transaction.date}</td>
                  <td className={`py-3 px-4 text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>{transaction.description}</td>
                  <td className={`py-3 px-4 text-gray-600 dark:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>{transaction.agentName}</td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      transaction.type === 'income' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}>
                      {t(`accountStatement.${transaction.type}`)}
                    </span>
                  </td>
                  <td className={`py-3 px-4 ${isRTL ? 'text-left' : 'text-right'} font-medium ${
                    transaction.type === 'income' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    ${transaction.amount.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-gray-600 dark:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>{transaction.paymentType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccountStatement;